import React, { useCallback, useEffect, useRef } from 'react';
import { motion, useAnimate } from 'motion/react';
import { EASE_OUT } from './authMotion';
import usePrefersReducedMotion from '../../hooks/usePrefersReducedMotion';

const BEAT_POINTS = '0,25 12,25 18,12 24,38 30,25 42,25 48,8 54,42 60,25 78,25 100,25';

export default function BrandBeatMark({ size = 168, color = '#ffffff', className = '' }) {
  const reduced = usePrefersReducedMotion();
  const [scope, animate] = useAnimate();
  const playing = useRef(false);
  const stroke = typeof size === 'number' ? Math.max(2.25, size * 0.018) : 2.5;
  const boxStyle =
    typeof size === 'number' ? { width: size, height: size } : undefined;
  const boxClass =
    typeof size === 'number'
      ? 'relative flex items-center justify-center'
      : `relative flex items-center justify-center ${size}`;

  const playBeat = useCallback(
    async ({ withIntro = false } = {}) => {
      if (reduced || playing.current || !scope.current) return;
      playing.current = true;

      try {
        if (withIntro) {
          await animate(
            scope.current,
            { opacity: 1, scale: 1 },
            { duration: 0.32, ease: EASE_OUT },
          );
        }

        animate(
          '[data-beat-ring]',
          { scale: [0.88, 1.08, 1], opacity: [0, 0.55, 0] },
          { duration: 1.1, ease: EASE_OUT },
        );

        animate(
          '[data-beat-disc]',
          { scale: [0.94, 1.04, 1], opacity: [0.85, 1, 1] },
          { duration: 0.42, ease: EASE_OUT },
        );

        await animate(
          '[data-beat-path]',
          { pathLength: [0, 1], opacity: [0.35, 1] },
          { duration: 0.9, ease: EASE_OUT, delay: withIntro ? 0.08 : 0 },
        );
      } finally {
        playing.current = false;
      }
    },
    [animate, reduced, scope],
  );

  const playBeatRef = useRef(playBeat);
  playBeatRef.current = playBeat;

  useEffect(() => {
    if (reduced) return undefined;
    void playBeatRef.current({ withIntro: true });
    return undefined;
  }, [reduced]);

  if (reduced) {
    return (
      <div
        className={`${boxClass} rounded-full bg-white/12 ${className}`.trim()}
        style={boxStyle}
        aria-hidden="true"
      >
        <svg viewBox="0 0 100 50" className="h-[42%] w-[70%]" fill="none">
          <polyline
            points={BEAT_POINTS}
            stroke={color}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    );
  }

  return (
    <motion.button
      ref={scope}
      type="button"
      onClick={() => {
        void playBeat();
      }}
      className={`${boxClass} cursor-pointer rounded-full border-0 bg-transparent p-0 outline-none focus-visible:ring-2 focus-visible:ring-white/50 ${className}`.trim()}
      style={boxStyle}
      initial={{ opacity: 0, scale: 0.92 }}
      whileTap={{ scale: 0.94 }}
      aria-label="Play heartbeat animation"
    >
      <div
        data-beat-ring
        className="pointer-events-none absolute inset-0 rounded-full bg-white/10"
        style={{ opacity: 0, transform: 'scale(0.88)' }}
      />
      <div
        data-beat-disc
        className="pointer-events-none absolute inset-[6%] rounded-full border border-white/25 bg-white/12"
        style={{ opacity: 0.85, transform: 'scale(0.94)' }}
      />
      <svg viewBox="0 0 100 50" className="pointer-events-none relative z-[1] h-[42%] w-[70%]" fill="none">
        <motion.polyline
          data-beat-path
          points={BEAT_POINTS}
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={{ pathLength: 0, opacity: 0.35 }}
        />
      </svg>
    </motion.button>
  );
}
